import baseArtwork from '../content/artwork.json';
import baseSite from '../content/site.json';
import type { ArtworkManifest, ArtworkPiece } from './artwork';
import type { SiteContent } from './site';

export interface RuntimeContent {
  site: SiteContent;
  artwork: ArtworkManifest;
  source: 'static' | 'live';
  updatedAt?: string;
}

const CONTENT_EVENT = 'su-content-changed';

let runtime: RuntimeContent = {
  site: baseSite as SiteContent,
  artwork: baseArtwork as ArtworkManifest,
  source: 'static',
};

export function getRuntimeContent(): RuntimeContent {
  return runtime;
}

export function getRuntimeSite(): SiteContent {
  return runtime.site;
}

export function getRuntimeArtwork(): ArtworkManifest {
  return runtime.artwork;
}

export function setRuntimeContent(partial: {
  site?: SiteContent;
  artwork?: ArtworkManifest;
  source?: 'static' | 'live';
  updatedAt?: string;
}) {
  runtime = {
    site: partial.site ?? runtime.site,
    artwork: partial.artwork ?? runtime.artwork,
    source: partial.source ?? runtime.source,
    updatedAt: partial.updatedAt ?? runtime.updatedAt,
  };
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CONTENT_EVENT));
  }
}

export function subscribeContent(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener(CONTENT_EVENT, cb);
  return () => window.removeEventListener(CONTENT_EVENT, cb);
}

/** Load live overlay from worker (KV). Falls back to static bundle. */
export async function hydrateRuntimeContent(): Promise<RuntimeContent> {
  try {
    const res = await fetch('/api/content', {
      headers: { accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) return runtime;
    const data = (await res.json()) as {
      ok?: boolean;
      site?: SiteContent;
      artwork?: ArtworkManifest;
      updatedAt?: string;
    };
    if (!data.ok) return runtime;
    if (data.site || data.artwork) {
      const nextUpdated = data.updatedAt ?? runtime.updatedAt;
      // Skip noisy re-renders when nothing changed
      if (
        runtime.source === 'live' &&
        nextUpdated &&
        runtime.updatedAt === nextUpdated &&
        data.artwork?.version === runtime.artwork.version
      ) {
        return runtime;
      }
      setRuntimeContent({
        site: data.site ?? runtime.site,
        artwork: mergeArtwork(baseArtwork as ArtworkManifest, data.artwork),
        source: 'live',
        updatedAt: data.updatedAt,
      });
    }
  } catch {
    /* keep static */
  }
  return runtime;
}

/** Keep public pages fresh when Mom (or anyone) updates live content. */
export function startContentAutoRefresh(intervalMs = 20_000): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const tick = () => {
    void hydrateRuntimeContent();
  };
  const onVis = () => {
    if (document.visibilityState === 'visible') tick();
  };
  const onFocus = () => tick();
  const id = window.setInterval(tick, intervalMs);
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('focus', onFocus);
  return () => {
    window.clearInterval(id);
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('focus', onFocus);
  };
}

function mergeArtwork(base: ArtworkManifest, overlay?: ArtworkManifest): ArtworkManifest {
  if (!overlay?.pieces?.length) return base;
  const byId = new Map<string, ArtworkPiece>();
  for (const p of base.pieces) byId.set(p.id, p);
  for (const p of overlay.pieces) byId.set(p.id, { ...byId.get(p.id), ...p } as ArtworkPiece);
  return { version: overlay.version ?? base.version, pieces: [...byId.values()] };
}

export { CONTENT_EVENT };
